import React, { Suspense, useEffect, useRef } from "react";
import YouTube from "react-youtube";
import { useInView } from "react-intersection-observer";
import { formatter, getTimeStamp } from "../../../utils/timestamp-utils";
import { useSelector } from "react-redux";
import get from "lodash/get";
import { SocialShare } from "@quintype/components";
import { SocialShareTemplate } from "../../Molecules/SocialShareTemplate";
import { isMobile } from "react-device-detect";
import { getPublishedDate, getPublishedTime } from "../../../../components/utils";

import "./yt-dialog.m.css";

const ListingAdSlot = React.lazy(() => import("../../../../components/molecules/ListingAdSlot"));

function LazyAdSlot({ index }) {
  const { ref, inView } = useInView({
    triggerOnce: true,
    rootMargin: "300px 0px", // top margin of 300px
    threshold: 0.01, // when 10% is in view
  });

  return inView ? (
    <Suspense fallback={<div>Loading ads...</div>}>
      <ListingAdSlot
        adStyleName="ad-slot-size-300x250"
        adType="right_side"
        pageType="ytshorts-page"
        adSlotConfigName="300x250_ad"
        index={index}
        showText={false}
      />
    </Suspense>
  ) : (
    <div ref={ref} style={{ height: 100 }}>
      Scroll to load component...
    </div>
  );
}

const shareClickMobile = async (story) => {
  const shareData = {
    text: story.headline,
    url: window.location.href, // or any custom URL
  };
  if (navigator.share) {
    try {
      await navigator.share(shareData);
      console.log("Share successful");
    } catch (err) {
      console.error("Share failed:", err.message);
    }
  } else {
    alert("Web Share is not supported on this browser.");
  }
};

const SocialShareComponent = ({ story, url }) => {
  if (isMobile) {
    return (
      <>
        <button styleName="share-button" aria-label="Share video" onClick={() => shareClickMobile(story)}>
          <svg xmlns="http://www.w3.org/2000/svg" height="24" viewBox="0 0 24 24" width="24" fill="#000">
            <path d="M0 0h24v24H0z" fill="none"></path>
            <path
              d="M18 16.08c-.76 0-1.44.3-1.96.77L8.91 12.7c.05-.23.09-.46.09-.7s-.04-.47-.09-.7l7.05-4.11c.54.5 1.25.81 2.04.81
                             1.66 0 3-1.34 3-3s-1.34-3-3-3-3 1.34-3 3c0 .24.04.47.09.7L8.04 9.81C7.5 9.31 6.79 9 6 9c-1.66 0-3 1.34-3 3s1.34 3
                             3 3c.79 0 1.5-.31 2.04-.81l7.12 4.16c-.05.21-.08.43-.08.65 0 1.61 1.31 2.92 2.92 2.92s2.92-1.31 2.92-2.92-1.31-2.92-2.92-2.92
                             M18 4c.55 0 1 .45 1 1s-.45 1-1 1-1-.45-1-1 .45-1 1-1M6 13c-.55 0-1-.45-1-1s.45-1 1-1 1 .45 1 1-.45 1-1 1m12 7.02c-.55 0-1-.45-1-1s.45-1
                             1-1 1 .45 1 1-.45 1-1 1"
            ></path>
          </svg>
          <span styleName="share-text">Share</span>
        </button>
      </>
    );
  }

  return (
    <div id={`story-share-${story.video_id}`} className="content-style">
      <SocialShare
        template={SocialShareTemplate}
        fullUrl={encodeURI(url)}
        title={story.headline}
        shareIconId={`share-icon-story-share-${story.video_id}`}
      />
    </div>
  );
};

const YTDialog = ({ story, closeDialog, dialogRef, isDialogOpen, isManualClicked }) => {
  const time = story["last-published-at"] || story["first-published-at"];
  const config = useSelector((state) => get(state, ["qt", "config"]));
  const isLocalizedNumber = useSelector((state) => get(state, ["qt", "config", "isLocalizedNumber"], false));
  const languageCode = isLocalizedNumber
    ? useSelector((state) => get(state, ["qt", "config", "language", "ietf-code"], "en"))
    : "en";

  const url = useSelector((state) => get(state, ["qt", "currentHostUrl"])) + story.slug;

  const playerRef = useRef(null);

  // Stop the video when modal closes
  useEffect(() => {
    if (!isDialogOpen && playerRef.current) {
      playerRef.current.stopVideo();
    }

    if (isManualClicked) {
      const contentId = story ? story.video_id : "NA";
      const templateName = story ? "detail" : "listing";
      const publishDate = story ? getPublishedDate(story?.["first-published-at"]) : "NA";
      const publishTime = story ? getPublishedTime(story?.["first-published-at"]) : "NA";
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: "dl_page_info",
        cms_author_name: "NA",
        byline: "NA",
        category: "short-videos",
        sub_category: "NA",
        content_id: contentId,
        template_name: templateName,
        publish_date: publishDate,
        publish_time: publishTime,
        login_status: "not-logged-in", // will be changed
        subscription_status: "not-subscribed", // will be changed
        ad_free: false, // will be changed
        login_wall_enabled: false,
        platform: window.innerWidth >= 768 ? "Desktop" : "Mobile",
      });
    }
  }, [isDialogOpen]);

  const onPlayerReady = (event) => {
    playerRef.current = event.target;
    event.target.playVideo();
  };

  useEffect(() => {
    const dialog = dialogRef.current;

    if (!dialog) return;

    // This is the key: prevent the default closing behavior
    const handleCancel = (event) => {
      event.preventDefault(); // Stop the dialog from closing natively
      // Now, you can trigger your own close logic or push a history state
      console.log("Dialog cancel event prevented!");
      closeDialog();
      // onClose(); // You might still want to call onClose if this is how your component manages it
      // Instead of direct close, you might want to handle history here:
      // if (window.history.state && window.history.state.dialogOpen) {
      //   window.history.back(); // Go back to the previous history state
      // } else {
      //   onClose(); // Fallback if no specific history state
      // }
    };

    dialog.addEventListener("cancel", handleCancel);

    return () => {
      dialog.removeEventListener("cancel", handleCancel);
    };
  }, [isDialogOpen]); // Re-run effect if isOpen or onClose changes

  function getRandomInteger(min, max) {
    min = Math.ceil(min);
    max = Math.floor(max);
    return Math.floor(Math.random() * (max - min)) + min;
  }

  const randomNumberInRange = getRandomInteger(1, 10000); // Generates a number between 1 and 10000

  return (
    <>
      <dialog id="videoModal" styleName="video-modal" aria-modal="true" aria-labelledby="modal-title" ref={dialogRef}>
        <div styleName="modal-container">
          <header styleName="modal-header">
            <div styleName="header-left">
              <button styleName="close-button" onClick={closeDialog} autoFocus={false}>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="black"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M18 6L6 18M6 6l12 12"></path>
                </svg>
              </button>

              <h2 styleName="modal-title truncate" id="modal-title">
                {story.headline}
              </h2>
              <a href="/">
                <svg
                  styleName="modal-logo"
                  width="119"
                  height="39"
                  viewBox="0 0 119 39"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M100.901.001h-4.318v15.146L92.2.001h-4.32v27.457h4.32V14.173l3.924 13.285h4.777zm-96.453 0H0v27.457h4.448V15.842h4.11v-4.59h-4.11v-6.66h6.402V.001zm19.283 20.833c0 .96-.76 1.782-1.793 1.782a1.777 1.777 0 0 1-1.795-1.782V6.03c0-.96.76-1.782 1.795-1.782.965 0 1.793.755 1.793 1.782zM21.938 0c-3.45.07-6.417 2.467-6.417 5.894v14.94c0 3.495 2.829 6.304 6.347 6.304s6.347-2.809 6.347-6.304V5.894c0-3.494-2.759-5.893-6.277-5.893zm29.87 4.524h4.07v22.203h4.554V4.524h4V0H51.81zm26.008 16.31c0 .96-.76 1.782-1.794 1.782a1.776 1.776 0 0 1-1.794-1.782V.001h-4.552v20.833c0 3.495 2.83 6.304 6.347 6.304s6.347-2.809 6.347-6.304V.001h-4.553zM119 4.524V0h-11.107v26.726H119v-4.523h-6.553v-6.921h4.208V10.76h-4.209V4.524zm-72.228 5.894V6.032a6.287 6.287 0 0 0-6.277-6.03h-6.278v26.725h4.553V4.524h1.725c1.034 0 1.793.822 1.793 1.781v4.181c0 3.015-2.897 3.563-2.897 3.563l3.38 12.678h4.691l-3.174-10.553c1.586-1.234 2.484-3.084 2.484-5.756m69.4 25.355.482-3.22.07-1.234h.068l.07 1.234.413 3.22zm-.138-6.374-1.586 8.91h1.31l.206-1.37h1.519l.206 1.371H119l-1.586-8.908h-1.38zm-4.139 8.91h1.31V29.4h-1.31zm-2.829-1.439c0 .137-.206.274-.345.274h-1.104v-6.647h1.104c.207 0 .345.206.345.342zm.828-7.058c-.207-.275-.553-.413-.897-.413h-2.691v8.91h2.691c.345 0 .69-.138.897-.343.276-.273.414-.616.414-.96v-6.373a.97.97 0 0 0-.414-.821m-6.416 2.33.208 3.631h-.07l-.345-1.302-1.655-5.071h-1.243v8.909h1.243v-2.74l-.207-3.633h.069l.345 1.303 1.655 5.07h1.243V29.4h-1.243zm-6.001 6.167h1.31V29.4h-1.31z"
                    fill="#000"
                  />
                </svg>
              </a>
            </div>

            <div>
              <div styleName="button-group">
                <SocialShareComponent story={story} url={url} />

                <a
                  href="https://www.youtube.com/@fortuneindia6059"
                  target="_blank"
                  styleName="subscribe-button"
                  title="Open Magazine Youtube Channel"
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    textDecoration: "none",
                  }}
                  rel="noreferrer"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="32"
                    height="32"
                    viewBox="0 0 48 48"
                    style={{ marginRight: "8px" }}
                  >
                    <path
                      fill="#FF3D00"
                      d="M43.2,33.9c-0.4,2.1-2.1,3.7-4.2,4c-3.3,0.5-8.8,1.1-15,1.1c-6.1,0-11.6-0.6-15-1.1c-2.1-0.3-3.8-1.9-4.2-4C4.4,31.6,4,28.2,4,24c0-4.2,0.4-7.6,0.8-9.9c0.4-2.1,2.1-3.7,4.2-4C12.3,9.6,17.8,9,24,9c6.2,0,11.6,0.6,15,1.1c2.1,0.3,3.8,1.9,4.2,4c0.4,2.3,0.9,5.7,0.9,9.9C44,28.2,43.6,31.6,43.2,33.9z"
                    ></path>
                    <path fill="#FFF" d="M20 31L20 17 32 24z"></path>
                  </svg>
                  Subscribe Now
                </a>
              </div>
            </div>

            <div styleName="modal-player-icon">
              <a
                href="https://www.youtube.com/@fortuneindia6059"
                target="_blank"
                title="Open Magazine Youtube Channel"
                rel="noreferrer"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 48 48">
                  <path
                    fill="#FF3D00"
                    d="M43.2,33.9c-0.4,2.1-2.1,3.7-4.2,4c-3.3,0.5-8.8,1.1-15,1.1c-6.1,0-11.6-0.6-15-1.1c-2.1-0.3-3.8-1.9-4.2-4C4.4,31.6,4,28.2,4,24c0-4.2,0.4-7.6,0.8-9.9c0.4-2.1,2.1-3.7,4.2-4C12.3,9.6,17.8,9,24,9c6.2,0,11.6,0.6,15,1.1c2.1,0.3,3.8,1.9,4.2,4c0.4,2.3,0.9,5.7,0.9,9.9C44,28.2,43.6,31.6,43.2,33.9z"
                  ></path>
                  <path fill="#FFF" d="M20 31L20 17 32 24z"></path>
                </svg>
              </a>
            </div>
          </header>
          <div styleName="modal-content">
            <div styleName="modal-left">
              <div styleName="modal-text-block">
                <nav styleName="modal-breadcrumbs" aria-label="Breadcrumb">
                  <ol>
                    <li>
                      <a href="/">Home</a>
                    </li>
                    <li>
                      <a href="/short-videos">Short Videos</a>
                    </li>
                    <li aria-current="page" styleName="breadcrumb-video-title">
                      {story.headline}
                    </li>
                  </ol>
                </nav>
                <h2 styleName="modal-title truncate" id="modal-title-left">
                  {story.headline}
                </h2>
                <span styleName="modal-duration">{getTimeStamp(time, formatter, config, languageCode)}</span>
              </div>
            </div>
            <div styleName="video-container">
              {isDialogOpen && (
                <YouTube
                  id="videoFrame"
                  videoId={story.video_id}
                  opts={{
                    width: "100%",
                    height: "100%",
                    playerVars: {
                      autoplay: 1,
                      mute: 0,
                      controls: 1,
                      rel: 0,
                      modestbranding: 1,
                      playsinline: 1,
                    },
                  }}
                  onReady={onPlayerReady}
                />
              )}
            </div>
            <div styleName="modal-meta-info">
              <nav styleName="modal-breadcrumbs" aria-label="Breadcrumb">
                <ol>
                  <li>
                    <a href="/">Home</a>
                  </li>
                  <li>
                    <a href="/short-videos">Short Videos</a>
                  </li>
                  <li aria-current="page" styleName="breadcrumb-video-title-mobile">
                    {story.headline}
                  </li>
                </ol>
              </nav>
              <h2 styleName="modal-title" id="modal-title-mobile">
                {story.headline}
              </h2>
              <span styleName="modal-duration">{getTimeStamp(time, formatter, config, languageCode)}</span>
            </div>
            <div styleName="modal-right">
              <div styleName="ad-placeholder">
                <LazyAdSlot index={randomNumberInRange} />
              </div>
            </div>
          </div>
        </div>
      </dialog>
    </>
  );
};

export { YTDialog };
